/* Webmail's dialogs: move to folder, confirmations, folder names, and the keyboard help. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cx } from "../lib/cx";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { Field, Input } from "../ui/Field";
import { IconFolder } from "./icons";
import { Kbd } from "./parts";
import type { Folder } from "./types";

/** "Move to": type to narrow it down, arrows and Enter to choose. Opens with v. */
export function MovePicker({ open, folders, exclude, onPick, onClose }: { open: boolean; folders: Folder[]; exclude: string | null; onPick: (f: Folder) => void; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const list = useMemo(
    () => folders.filter((f) => f.path !== exclude && f.role !== "drafts" && f.role !== "sent" && (!q.trim() || f.name.toLowerCase().includes(q.trim().toLowerCase()) || f.path.toLowerCase().includes(q.trim().toLowerCase()))),
    [folders, exclude, q],
  );
  useEffect(() => {
    if (open) {
      setQ("");
      setHi(0);
    }
  }, [open]);
  useEffect(() => setHi(0), [q]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} title="Move to" size="sm" align="top">
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Folder name"
        aria-label="Find a folder"
        data-autofocus
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHi((h) => Math.min(h + 1, list.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHi((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter" && list[hi]) {
            e.preventDefault();
            onPick(list[hi]!);
          }
        }}
      />
      <ul className="mt-3 max-h-72 overflow-y-auto" role="listbox" aria-label="Folders">
        {list.map((f, i) => (
          <li key={f.path} role="option" aria-selected={i === hi}>
            <button
              type="button"
              onClick={() => onPick(f)}
              onMouseEnter={() => setHi(i)}
              className={cx("flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-left text-[14px]", i === hi ? "bg-selected text-selected-fg" : "text-body")}
              style={f.depth ? { paddingLeft: 10 + f.depth * 14 } : undefined}
            >
              <IconFolder size={15} className="text-dim" />
              {f.name}
            </button>
          </li>
        ))}
        {!list.length ? <li className="px-2.5 py-3 text-sm text-dim">No folder matches.</li> : null}
      </ul>
    </Dialog>
  );
}

export type ConfirmState = { title: string; body: ReactNode; action: string; danger?: boolean; run: () => Promise<unknown> | void };

export function Confirm({ state, onClose }: { state: ConfirmState | null; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open={!!state}
      onOpenChange={(o) => !o && !busy && onClose()}
      title={state?.title ?? ""}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={state?.danger ? "danger" : "primary"}
            loading={busy}
            data-autofocus
            onClick={async () => {
              if (!state) return;
              setBusy(true);
              try {
                await state.run();
              } finally {
                setBusy(false);
                onClose();
              }
            }}
          >
            {state?.action}
          </Button>
        </>
      }
    >
      <div className="text-[14px] leading-relaxed text-body">{state?.body}</div>
    </Dialog>
  );
}

export function FolderNameDialog({
  open,
  title,
  initial,
  action,
  onSubmit,
  onClose,
}: {
  open: boolean;
  title: string;
  initial: string;
  action: string;
  onSubmit: (name: string) => Promise<string | null>;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) {
      setName(initial);
      setError(null);
    }
  }, [open, initial]);
  const submit = async () => {
    setBusy(true);
    const err = await onSubmit(name);
    setBusy(false);
    if (err) setError(err);
    else onClose();
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            {action}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Folder name" error={error}>
          <Input ref={ref} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
        </Field>
      </form>
    </Dialog>
  );
}

const KEYS: [string[], string][] = [
  [["/"], "Search"],
  [["c"], "Compose"],
  [["j"], "Next conversation"],
  [["k"], "Previous conversation"],
  [["o", "Enter"], "Open"],
  [["u"], "Back to the list"],
  [["x"], "Select"],
  [["g", "i"], "Go to Inbox"],
  [["g", "s"], "Go to Starred"],
];
const ACT: [string[], string][] = [
  [["Shift", "click"], "Select a range"],
  [["e"], "Archive"],
  [["#"], "Delete"],
  [["!"], "Spam"],
  [["s"], "Star"],
  [["Shift", "I"], "Mark read"],
  [["Shift", "U"], "Mark unread"],
  [["v"], "Move to a folder"],
  [["r"], "Reply"],
  [["a"], "Reply all"],
  [["f"], "Forward"],
];

export function HelpOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  const table = (title: string, rows: [string[], string][]) => (
    <div>
      <h3 className="mb-2 text-[13px] font-semibold text-fg">{title}</h3>
      <dl className="space-y-1.5">
        {rows.map(([keys, what]) => (
          <div key={what + keys.join()} className="flex items-center justify-between gap-3 text-[13px]">
            <dt className="text-body">{what}</dt>
            <dd className="flex shrink-0 items-center gap-1">
              {keys.map((k, i) => (
                <span key={k} className="inline-flex items-center gap-1">
                  {i > 0 ? <span className="text-[11px] text-faint">{["Shift", "⌘", "Ctrl"].includes(keys[0]!) ? "+" : keys[0] === "o" ? "or" : "then"}</span> : null}
                  <Kbd>{k}</Kbd>
                </span>
              ))}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} title="Keyboard shortcuts" size="lg">
      <div className="grid gap-8 sm:grid-cols-2">
        {table("Reading and moving", KEYS)}
        {table("Acting on a conversation", ACT)}
      </div>
      <div className="mt-6 grid gap-8 sm:grid-cols-2">
        {table("Anywhere", [
          [[isMac ? "⌘" : "Ctrl", "Enter"], "Send (in compose)"],
          [["Esc"], "Close"],
          [["?"], "This list"],
          [["drag"], "Drag conversations onto a folder to move them"],
        ])}
        <div>
          <h3 className="mb-2 text-[13px] font-semibold text-fg">Search</h3>
          <ul className="space-y-1 font-mono text-[12px] text-body">
            <li>from:ann to:bob subject:invoice</li>
            <li>has:attachment is:unread is:starred</li>
            <li>before:2026-10-01 after:2026-09-01</li>
            <li>in:archive "exact words"</li>
          </ul>
        </div>
      </div>
    </Dialog>
  );
}
